import { AnimatePresence, Variants, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useState } from "react";
import useSWR from "swr";
import LoadingSpinner from "@components/loadingSpinner";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useIsShopsite } from "@context/DomainContext";
import { postDeleteTickets } from "@features/cartDetails/utils/deleteTicketsUtil";
import { CheckoutDetails } from "@features/checkout/types";
import { useNotificationStore } from "@store/notificationStore";
import { fetcher } from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import { useLatestCookieValue, useWindowSize } from "@utils/hooks";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import { Button, Card, Heading, Text } from "@components/theming";
import { Analytics } from "@types";

interface Props {
  uniqueId: number;
  minimum_quantity: number;
  is_last_ticket?: boolean;
  other_tickets_to_delete?: number[];
  eventId: number;
  showingId: number;
  eventName: string;
  priceAgeId: number;
  type: string;
  price: number;
  analyticsConfigs?: Analytics[];
  canTrack?: boolean;
}

const DeleteTicket = ({
  uniqueId,
  minimum_quantity,
  is_last_ticket = false,
  other_tickets_to_delete,
  eventId,
  showingId,
  eventName,
  priceAgeId,
  type,
  price,
  analyticsConfigs,
  canTrack,
}: Props) => {
  const windowSize = useWindowSize();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const [open, setOpen] = useState(false);
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const deleteTranslations = useTranslations("Checkout.Cart.tickets.delete");
  const [loading, setLoading] = useState(false);
  const isShopsite = useIsShopsite();

  const { data: checkoutDetails } = useSWR<CheckoutDetails>(
    basketKey ? `basket/${basketKey}/checkout` : null,
    fetcher
  );

  const variants: Variants =
    windowSize.width && windowSize.width >= 640
      ? {
          hidden: {
            opacity: 0,
            scale: 0.9,
            transition: { duration: 0.125 },
          },
          show: {
            opacity: 1,
            scale: 1,
          },
        }
      : {
          hidden: {
            y: 200,
            opacity: 0,
          },
          show: {
            y: 0,
            opacity: 1,
          },
        };

  const handleDeleteTicket = async (uniqueId: number) => {
    setLoading(true);
    await postDeleteTickets(
      uniqueId,
      is_last_ticket,
      other_tickets_to_delete,
      setSuccess,
      setFailure,
      eventId,
      showingId,
      eventName,
      priceAgeId,
      type,
      price,
      checkoutDetails?.Currency.CurrencyCode ?? "",
      analyticsConfigs,
      canTrack,
      deleteTranslations
    );

    setLoading(false);
  };

  if (other_tickets_to_delete) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger
          aria-label={deleteTranslations("removeticket")}
          disabled={loading || checkoutDetails?.IsPended}
        >
          {loading ? (
            <LoadingSpinner
              size="20"
              color={!isShopsite ? "var(--gray-11)" : undefined}
            />
          ) : (
            <TrashIcon
              aria-hidden="true"
              className={joinClassNames(
                "w-5 h-5 sm:w-6 sm:h-6",
                checkoutDetails?.IsPended
                  ? isShopsite
                    ? "text-red-200 cursor-not-allowed"
                    : "text-[color:var(--red-a6)] cursor-not-allowed"
                  : isShopsite
                  ? "text-red-500"
                  : "text-[color:var(--red-9)]"
              )}
            />
          )}
        </DialogTrigger>
        <AnimatePresence mode="wait">
          {open && (
            <DialogContent
              asChild
              forceMount
              handleClose={() => setOpen(false)}
            >
              <Card asChild shopsiteClasses="rounded-3xl overflow-hidden">
                <motion.div
                  data-testid="clearcartcontent"
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={variants}
                  transition={
                    windowSize.width && windowSize.width >= 640
                      ? { duration: 0.25 }
                      : { duration: 0.2 }
                  }
                  className="flex flex-col"
                >
                  <div
                    className={joinClassNames(
                      "flex flex-col gap-4 pb-4",
                      isShopsite ? "px-6 pt-6 gap-4 bg-red-500" : ""
                    )}
                  >
                    <div className="relative self-end">
                      <DialogClose asChild>
                        <DialogCloseButton shopsiteVariant="white" />
                      </DialogClose>
                    </div>
                    <DialogHeader className="!text-left">
                      <DialogTitle asChild>
                        <Heading
                          as="h3"
                          size="5"
                          weight="bold"
                          shopsiteClasses="text-white text-xl font-bold"
                        >
                          {deleteTranslations("heading")}
                        </Heading>
                      </DialogTitle>
                      <TrashIcon
                        aria-hidden="true"
                        className={joinClassNames(
                          "absolute top-0 left-4 w-24 h-24 -rotate-12",
                          isShopsite
                            ? "text-white/10"
                            : " text-[color:var(--red-a3)]"
                        )}
                      />
                    </DialogHeader>
                  </div>
                  <Text color="gray" shopsiteClasses="text-gray-600 px-6 pt-4">
                    {deleteTranslations("description", {
                      min_quantity: minimum_quantity,
                    })}
                  </Text>
                  <div
                    className={joinClassNames(
                      "flex-1 flex items-center justify-between gap-6 mt-4",
                      isShopsite ? "px-6 pb-6" : ""
                    )}
                  >
                    <DialogClose asChild>
                      <Button
                        shopsiteClasses="!bg-gray-100 hover:!bg-gray-500 !text-gray-800 hover:!text-white"
                        color="gray"
                        size="3"
                        sharedClasses="grow"
                      >
                        {deleteTranslations("cancel")}
                      </Button>
                    </DialogClose>
                    <DialogClose
                      asChild
                      data-testid="deletecart"
                      onClick={async () => {
                        await handleDeleteTicket(uniqueId);
                      }}
                    >
                      <Button
                        color="red"
                        size="3"
                        sharedClasses="grow"
                        shopsiteClasses="bg-red-500 hover:bg-red-400 text-white"
                      >
                        {deleteTranslations("delete")}
                      </Button>
                    </DialogClose>
                  </div>
                </motion.div>
              </Card>
            </DialogContent>
          )}
        </AnimatePresence>
      </Dialog>
    );
  }

  return (
    <button
      data-testid={`deleteticket`}
      aria-label={deleteTranslations("removeticket")}
      disabled={loading || checkoutDetails?.IsPended}
      onClick={async () => {
        await handleDeleteTicket(uniqueId);
      }}
    >
      {loading ? (
        <LoadingSpinner
          size="20"
          color={!isShopsite ? "var(--gray-11)" : undefined}
        />
      ) : (
        <TrashIcon
          aria-hidden="true"
          className={joinClassNames(
            "w-5 h-5 sm:w-6 sm:h-6",
            checkoutDetails?.IsPended
              ? isShopsite
                ? "text-red-200 cursor-not-allowed"
                : "text-[color:var(--red-a6)] cursor-not-allowed"
              : isShopsite
              ? "text-red-500"
              : "text-[color:var(--red-9)]"
          )}
        />
      )}
    </button>
  );
};

export default DeleteTicket;
